import StyledModal from "../Modal/StyledModalView";
import "./timesheet-form.style.css";
import Loader from "../Loader/Loader";
import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";

function TimesheetFormView({
    hours,
    meters,
    extraWork,
    materials,
    projects,
    selectedProject,
    setSelectedProject,
    user,
    loadingUser,
    loggedInUser,
    targetUser,
    projectUsers,
    selectedTargetUserId,
    handleTargetUserChange,
    totalHours,
    totalCosts,
    rateType,
    submitting,
    showConfirmModal,
    setShowConfirmModal,
    units,
    daysOfWeek,
    getWeekRange,
    handlePreviousWeek,
    handleNextWeek,
    handleHoursChange,
    handleMetersChange,
    handleAddExtraWork,
    handleExtraWorkChange,
    handleRemoveExtraWork,
    getWeekNumber,
    handleAddMaterial,
    handleMaterialChange,
    handleMaterialFileChange,
    handleRemoveMaterial,
    handleGeneratePDF,
    handleSubmit,
    handleSaveAsDraft,
    initialTimesheet
}) {
    const { t } = useTranslation();
    const { startOfWeek, endOfWeek } = getWeekRange();

    // Helper function to translate day names
    const translateDay = (day) => {
        const dayMap = {
            'Zondag': 'sunday',
            'Maandag': 'monday',
            'Dinsdag': 'tuesday',
            'Woensdag': 'wednesday',
            'Donderdag': 'thursday',
            'Vrijdag': 'friday',
            'Zaterdag': 'saturday',
            'Sunday': 'sunday',
            'Monday': 'monday',
            'Tuesday': 'tuesday',
            'Wednesday': 'wednesday',
            'Thursday': 'thursday',
            'Friday': 'friday',
            'Saturday': 'saturday'
        };
        return t(`timesheet.${dayMap[day] || 'monday'}`);
    };

    if (loadingUser) return <Loader message={t('common.loading')} />;
    if (!selectedProject || !user) {
        return (
            <div className="timesheet-form">
                <p className="no-projects">{t('timesheet.notAssignedToProject')}</p>
                <NoDataConstruction text={t('timesheet.notAssignedToProject')}/>
            </div>
        );
    }

    return (
        <div className="form-container">
            <form onSubmit={handleSubmit} className="form-section timesheet-form--card">
                <div className="form-section-header">
                    <h2 className="form-section-title">
                        {t('timesheet.title')}: {targetUser?.user_name || user.user_name}
                    </h2>
                    <p className="form-section-description">
                        {startOfWeek.toLocaleDateString(t('common.locale'))} - {endOfWeek.toLocaleDateString(t('common.locale'))}
                        <span className="ml-2">
                            {t('timesheet.week')} {getWeekNumber(startOfWeek)}
                        </span>
                    </p>
                    <div className="timesheet-form__nav flex gap-2 mt-3">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handlePreviousWeek}
                            aria-label={`Vorige week (${getWeekNumber(startOfWeek) - 1})`}
                        >
                            &larr; Week {getWeekNumber(startOfWeek) - 1}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleNextWeek}
                            aria-label={`Volgende week (${getWeekNumber(startOfWeek) + 1})`}
                        >
                            Week {getWeekNumber(startOfWeek) + 1} &rarr;
                        </button>
                    </div>
                </div>

                <div className="form-grid">
                    <div className="form-group form-item-full">
                        <label htmlFor="project-select" className="form-label required">
                            {t('timesheet.project')}
                        </label>
                        <select
                            id="project-select"
                            className={`form-input ${!selectedProject?.id ? 'error' : ''}`}
                            value={selectedProject?.id || ""}
                            onChange={(e) => setSelectedProject(projects.find(p => p.id === parseInt(e.target.value)))}
                            aria-required="true"
                            aria-invalid={!selectedProject?.id}
                        >
                            <option value="" disabled>{t('timesheet.selectProjectFirst')}</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        {!selectedProject?.id && (
                            <div className="form-error" role="alert">
                                {t('timesheet.selectProjectToContinue')}
                            </div>
                        )}
                    </div>

                    {loggedInUser && (loggedInUser.role === "eigenaar" || loggedInUser.role === "financieel medewerker") && projectUsers.length > 0 && (
                        <div className="form-group form-item-full">
                            <label htmlFor="target-user-select" className="form-label">
                                {t('timesheet.fillTimesheetFor')}
                            </label>
                            <select
                                id="target-user-select"
                                className="form-input"
                                value={selectedTargetUserId || ""}
                                onChange={(e) => handleTargetUserChange(e.target.value)}
                            >
                                <option value="">-- {t('timesheet.selectUser')} --</option>
                                {projectUsers.map(pu => (
                                    <option key={pu.user_id} value={pu.user_id}>
                                        {pu.user_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="form-section mt-4">
                    <h3 className="form-section-title text-base mb-3">
                        {rateType === "meter" ? t('timesheet.meters') : t('timesheet.hours')}
                    </h3>
                    <div className="timesheet-form__grid form-grid-2">
                        {daysOfWeek.map((day, index) => {
                            const dayDate = new Date(startOfWeek);
                            dayDate.setDate(startOfWeek.getDate() + index);
                            const dateStr = dayDate.toLocaleDateString(t('common.locale'), { day: 'numeric', month: 'short' });

                            return (
                                <div key={day} className="form-group">
                                    <label htmlFor={`day-${index}`} className="form-label">
                                        <span className="font-semibold">{translateDay(day)}</span>
                                        <span className="text-muted ml-2 text-sm">{dateStr}</span>
                                    </label>
                                    <input
                                        id={`day-${index}`}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        inputMode="decimal"
                                        className="form-input"
                                        value={rateType === "meter" ? (meters[index] === 0 ? "" : meters[index]) : (hours[index] === 0 ? "" : hours[index])}
                                        onChange={(e) => rateType === "meter" ? handleMetersChange(index, e.target.value) : handleHoursChange(index, e.target.value)}
                                        placeholder={rateType === "meter" ? t('timesheet.numberOfMeters') : t('timesheet.numberOfHours')}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="form-section mt-4">
                    <h3 className="form-section-title text-base mb-3">
                        {t('timesheet.extraWork')}
                    </h3>
                    {extraWork.length === 0 && (
                        <p className="text-muted italic">{t('timesheet.noExtraWorkAdded')}</p>
                    )}
                    {extraWork.map((work, idx) => (
                        <div key={idx} className="form-grid form-grid-3 mb-3 p-3 rounded bg-elevated">
                            <div className="form-group">
                                <label htmlFor={`extra-desc-${idx}`} className="form-label">
                                    {t('timesheet.description')}
                                </label>
                                <input
                                    id={`extra-desc-${idx}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.description')}
                                    value={work.omschrijving || ''}
                                    onChange={(e) => handleExtraWorkChange(idx, "omschrijving", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`extra-notes-${idx}`} className="form-label">
                                    {t('timesheet.notes')}
                                </label>
                                <input
                                    id={`extra-notes-${idx}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.notes')}
                                    value={work.opmerkingen || ''}
                                    onChange={(e) => handleExtraWorkChange(idx, "opmerkingen", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`extra-work-file-${idx}`} className="form-label">
                                    {t('timesheet.photo')}
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    id={`extra-work-file-${idx}`}
                                    className="form-input"
                                    onChange={(e) => handleExtraWorkChange(idx, "foto", e)}
                                />
                                {work.foto && <span className="text-sm text-success mt-1">✓ {work.foto.name}</span>}
                                {work.preview && <span className="text-sm text-success mt-1">✓ {t('timesheet.photoSelected')}</span>}
                            </div>
                            <div className="form-group form-item-full">
                                <button
                                    type="button"
                                    className="btn btn-delete"
                                    onClick={() => handleRemoveExtraWork(idx)}
                                    aria-label={`${t('common.delete')} extra werk ${idx + 1}`}
                                >
                                    {t('common.delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-add"
                        onClick={handleAddExtraWork}
                    >
                        {t('timesheet.addExtraWork')}
                    </button>
                </div>

                <div className="form-section mt-4">
                    <h3 className="form-section-title text-base mb-3">
                        {t('timesheet.materials')}
                    </h3>
                    {materials.length === 0 && (
                        <p className="text-muted italic">{t('timesheet.noMaterialsAdded')}</p>
                    )}
                    {materials.map((m, i) => (
                        <div key={i} className="form-grid form-grid-3 mb-3 p-3 rounded bg-elevated">
                            <div className="form-group">
                                <label htmlFor={`material-brand-${i}`} className="form-label">
                                    {t('timesheet.brand')}
                                </label>
                                <input
                                    id={`material-brand-${i}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.brand')}
                                    value={m.name || ''}
                                    onChange={(e) => handleMaterialChange(i, "name", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`material-name-${i}`} className="form-label">
                                    {t('timesheet.materialName')}
                                </label>
                                <input
                                    id={`material-name-${i}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.materialName')}
                                    value={m.description || ''}
                                    onChange={(e) => handleMaterialChange(i, "description", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`material-qty-${i}`} className="form-label">
                                    {t('timesheet.quantity')}
                                </label>
                                <input
                                    id={`material-qty-${i}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.quantity')}
                                    value={m.quantity === 0 ? "" : m.quantity}
                                    onChange={(e) => handleMaterialChange(i, "quantity", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`material-unit-${i}`} className="form-label">
                                    {t('timesheet.unit')}
                                </label>
                                <select
                                    id={`material-unit-${i}`}
                                    className="form-input"
                                    value={m.unit}
                                    onChange={(e) => handleMaterialChange(i, "unit", e.target.value)}
                                >
                                    <option value="">{t('timesheet.selectUnit')}</option>
                                    {units.map((unit) => (
                                        <option key={unit} value={unit}>
                                            {unit === "m2" ? "m²" : unit.charAt(0).toUpperCase() + unit.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor={`material-cost-${i}`} className="form-label">
                                    {t('timesheet.costPerUnit')}
                                </label>
                                <input
                                    id={`material-cost-${i}`}
                                    type="text"
                                    className="form-input"
                                    placeholder={t('timesheet.costPerUnit')}
                                    value={m.cost === 0 ? "" : m.cost}
                                    onChange={(e) => handleMaterialChange(i, "cost", e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`material-file-${i}`} className="form-label">
                                    {t('timesheet.receipt')}
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    id={`material-file-${i}`}
                                    className="form-input"
                                    onChange={(e) => handleMaterialFileChange(i, e.target.files[0])}
                                />
                                {m.bonnetje && <span className="text-sm text-success mt-1">✓ {m.bonnetje.name || t('timesheet.receiptSelected')}</span>}
                            </div>
                            <div className="form-group form-item-full">
                                <button
                                    type="button"
                                    className="btn btn-delete"
                                    onClick={() => handleRemoveMaterial(i)}
                                    aria-label={`${t('common.delete')} materiaal ${i + 1}`}
                                >
                                    {t('common.delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-add"
                        onClick={handleAddMaterial}
                    >
                        {t('timesheet.addMaterial')}
                    </button>
                </div>

                <div className="form-section mt-4 p-4 rounded bg-elevated">
                    <h3 className="form-section-title text-base mb-3">
                        {t('timesheet.summary')}
                    </h3>
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                            <span className="text-muted">{t('timesheet.total')} {rateType === "meter" ? t('timesheet.meters').toLowerCase() : t('timesheet.hours').toLowerCase()}:</span>
                            <span className="font-bold text-lg">{totalHours}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted">{t('timesheet.totalMaterialCosts')}:</span>
                            <span className="font-bold text-lg">€{totalCosts.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleGeneratePDF}
                    >
                        {t('timesheet.downloadPDF')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={handleSaveAsDraft}
                        disabled={submitting}
                    >
                        {t('timesheet.saveAsDraft')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-submit"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={submitting}
                    >
                        {t('timesheet.submitForApproval')}
                    </button>
                </div>
            </form>

            <StyledModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title={initialTimesheet?.status === 'draft' ? t('timesheet.submitDraftForApproval') : t('timesheet.confirmSubmit')}
                confirmText={t('timesheet.yesSubmit')}
                closeText={t('common.cancel')}
                onConfirm={(e) => {
                    handleSubmit(e);
                    setShowConfirmModal(false);
                }}
            >
                {initialTimesheet?.status === 'draft' ? (
                    <p>{t('timesheet.confirmSubmitDraft')}</p>
                ) : (
                    <p>{t('timesheet.confirmSubmitMessage')}</p>
                )}
            </StyledModal>
        </div>
    );
}

TimesheetFormView.propTypes = {
    hours: PropTypes.arrayOf(PropTypes.number).isRequired,
    meters: PropTypes.arrayOf(PropTypes.number).isRequired,
    extraWork: PropTypes.arrayOf(PropTypes.shape({
        omschrijving: PropTypes.string,
        opmerkingen: PropTypes.string,
        foto: PropTypes.any
    })).isRequired,
    materials: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        quantity: PropTypes.number,
        unit: PropTypes.string,
        cost: PropTypes.number
    })).isRequired,
    projects: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    selectedProject: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        location: PropTypes.string,
        address: PropTypes.string
    }),
    setSelectedProject: PropTypes.func.isRequired,
    user: PropTypes.shape({
        user_name: PropTypes.string
    }),
    loadingUser: PropTypes.bool,
    loggedInUser: PropTypes.shape({
        id: PropTypes.number,
        role: PropTypes.string
    }),
    targetUser: PropTypes.shape({
        user_id: PropTypes.number,
        user_name: PropTypes.string,
        rate_type: PropTypes.string
    }),
    projectUsers: PropTypes.arrayOf(PropTypes.shape({
        user_id: PropTypes.number,
        user_name: PropTypes.string,
        rate_type: PropTypes.string
    })),
    selectedTargetUserId: PropTypes.number,
    handleTargetUserChange: PropTypes.func,
    totalHours: PropTypes.number.isRequired,
    totalCosts: PropTypes.number.isRequired,
    rateType: PropTypes.oneOf(['hourly', 'meter']).isRequired,
    submitting: PropTypes.bool,
    showConfirmModal: PropTypes.bool.isRequired,
    setShowConfirmModal: PropTypes.func.isRequired,
    units: PropTypes.arrayOf(PropTypes.string).isRequired,
    daysOfWeek: PropTypes.arrayOf(PropTypes.string).isRequired,
    getWeekRange: PropTypes.func.isRequired,
    handlePreviousWeek: PropTypes.func.isRequired,
    handleNextWeek: PropTypes.func.isRequired,
    handleHoursChange: PropTypes.func.isRequired,
    handleMetersChange: PropTypes.func.isRequired,
    handleAddExtraWork: PropTypes.func.isRequired,
    handleExtraWorkChange: PropTypes.func.isRequired,
    handleRemoveExtraWork: PropTypes.func.isRequired,
    getWeekNumber: PropTypes.func.isRequired,
    handleAddMaterial: PropTypes.func.isRequired,
    handleMaterialChange: PropTypes.func.isRequired,
    handleMaterialFileChange: PropTypes.func.isRequired,
    handleRemoveMaterial: PropTypes.func.isRequired,
    handleGeneratePDF: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleSaveAsDraft: PropTypes.func.isRequired,
    initialTimesheet: PropTypes.shape({
        id: PropTypes.number,
        status: PropTypes.string
    })
};

export default TimesheetFormView;
